import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import { Button, FormGroup, Label } from 'reactstrap';

const customStyles = (dropdownHeight) => ({
  control: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
    color: '#3e5569',
    border: '1px solid #dee2e6',
    backgroundColor: state.isDisabled ? '#f2f7f8' : 'white',
    borderRadius: '0.3rem',
    boxShadow: 'none',
    height: '34.5px',
    minHeight: '34.5px',
    '&:hover': {
      borderColor: '#dee2e6'
    },
    '&:focus': {
      borderColor: 'rgb(136.5, 145, 154)'
    }
  }),
  valueContainer: (base) => ({
    ...base,
    color: '#3e5569',
    padding: '0 0.75rem',
    maxHeight: '32.5px',
    overflowY: 'auto'
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999
  }),
  menuList: (base) => ({
    ...base,
    zIndex: 9999,
    maxHeight: dropdownHeight ? `${dropdownHeight}px` : '250px'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#2cabe3'
      : state.isFocused
        ? '#f0f0f0'
        : state.active
          ? '#3e5569'
          : null,
    color: state.isSelected ? '#ffffff' : '#3e5569'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#3e5569'
  }),
  indicatorsSeparator: (base) => ({
    ...base,
    display: 'none'
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: 'none'
  }),
  singleValue: (base) => ({
    ...base,
    color: '#3e5569'
  })
});

const FormSelect = ({
  className = '',
  inputClassName = '',
  label,
  name,
  value,
  options = [],
  placeholder,
  disabled,
  isShowLabel = true,
  touched,
  errors,
  onChange,
  onFocus,
  onBlur,
  isMulti = false,
  isGroup = false,
  dropdownHeight,
  menuPlacement = 'auto',
  setPlacementAuto = false,
  isAdd = false,
  createName = 'Create',
  onCreateClick
}) => {
  const isError = touched && errors && touched[name] && errors[name];

  const selectRef = useRef(null);
  const menuRef = useRef(null);
  const [isMenuPlacement, setIsMenuPlacement] = useState(menuPlacement);

  useEffect(() => {
    setIsMenuPlacement(menuPlacement);
  }, [menuPlacement]);

  const calculateMenuPlacement = () => {
    if (menuRef.current) {
      const menuPosition = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - menuPosition.bottom;
      const spaceAbove = menuPosition.top;
      setIsMenuPlacement(spaceBelow < 250 && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }
  };

  // Trigger placement calculation when the menu opens
  const onMenuOpen = () => {
    if (menuPlacement === 'auto' && setPlacementAuto) {
      calculateMenuPlacement();
    }
  };

  const getValue = () => {
    if (isGroup) {
      const optionArray = [];
      options?.map((x) => {
        const data = x?.options || [];
        optionArray.push(...data);
      });
      return optionArray.find((x) => x.value === value);
    } else {
      return options.find((x) => x.value === value);
    }
  };

  const getMultiValue = () => {
    const newOptions = [];
    if (isGroup) {
      const optionArray = [];
      options?.map((x) => {
        const data = x?.options || [];
        optionArray.push(...data);
      });
      optionArray.filter((opt) =>
        value?.map((x) => {
          if (x === opt.value) {
            newOptions.push(opt);
          }
        })
      );
    } else {
      options.filter((opt) =>
        value?.map((x) => {
          if (x === opt.value) {
            newOptions.push(opt);
          }
        })
      );
    }
    return newOptions;
  };

  const handleSelectAll = () => {
    const allValues = options.map((option) => option.value);
    const allSelected = value.length === allValues.length;
    const newValue = allSelected ? [] : allValues;
    onChange({
      target: {
        name,
        value: newValue,
        label: options.filter((opt) => newValue.includes(opt.value)).map((opt) => opt.label)
      }
    });
  };

  const enhancedOptions = isMulti
    ? [{ value: 'select-all', label: 'Select All' }, ...options]
    : options;

  const MenuList = ({ children, ...props }) => {
    return isAdd ? (
      <components.MenuList {...props}>
        {children}
        <div>
          <Button className="py-1 px-2 w-100 rounded-0" onClick={() => onCreateClick()}>
            {createName}
          </Button>
        </div>
      </components.MenuList>
    ) : (
      <components.MenuList {...props}>{children}</components.MenuList>
    );
  };

  MenuList.propTypes = {
    children: PropTypes.node
  };

  return (
    <FormGroup className={className}>
      {isShowLabel && <Label htmlFor={name}>{label}</Label>}
      <Select
        ref={selectRef}
        className={`react-select-container ${isError ? 'is-invalid' : ''} ${inputClassName}`}
        classNamePrefix="react-select"
        name={name}
        value={isMulti ? getMultiValue() : getValue() || ''}
        options={enhancedOptions}
        placeholder={placeholder}
        components={{ MenuList }}
        hideSelectedOptions={false}
        onChange={(selectedOption, action) => {
          if (action?.option?.value === 'select-all') {
            handleSelectAll();
          } else {
            onChange({
              target: {
                name,
                value: isMulti
                  ? selectedOption.map((option) => option.value)
                  : selectedOption?.value,
                label: isMulti
                  ? selectedOption.map((option) => option.label)
                  : selectedOption?.label
              }
            });
          }
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        isDisabled={disabled}
        isMulti={isMulti}
        menuPlacement={isMenuPlacement}
        styles={customStyles(dropdownHeight)}
        onMenuOpen={onMenuOpen} // Trigger placement calculation when menu opens
        menuRef={menuRef}
        aria-invalid={isError ? 'true' : 'false'}
        onMouseDown={(e) => e.stopPropagation()}
      />
      {isError && <span className="invalid-feedback">{errors[name]}</span>}
    </FormGroup>
  );
};

FormSelect.propTypes = {
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  ),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isShowLabel: PropTypes.bool,
  touched: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  isMulti: PropTypes.bool,
  isGroup: PropTypes.bool,
  setPlacementAuto: PropTypes.bool,
  dropdownHeight: PropTypes.number, // New prop for dynamic height
  menuPlacement: PropTypes.string, // New prop for dynamic height
  isAdd: PropTypes.bool,
  createName: PropTypes.string,
  onCreateClick: PropTypes.func
};

export default FormSelect;
